import React, { ComponentProps } from 'react'
import { DomainDto } from '../../dtos/domain'
import { UserDto } from '../../dtos/user'
import { AccountService, AppService, DomainService, UserService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { getClassNames } from '../../_utils/classnames'
import { simpleFormErrorNotification } from '../../_utils/utils'
import { AntdTableEmptyPlaceholder, Well } from '@pushly/aqe/lib/components'
import { TableRowEntityDisplay } from '../table-row-entity-display/table-row-entity-display'
import { InfoCircleOutlined, Loading3QuartersOutlined, WarningOutlined } from '@ant-design/icons'

import { AbilityAction } from '../../enums/ability-action.enum'
import { AppState } from '../../stores/app'
import AccessControlService from '../../services/access-control.service'
import {
    AccessRoleId,
    ExternalAccessRoleIds,
    ExternalApiAccessRoleIds,
    InternalAccessRoleIds,
} from '../../enums/access-role.enum'
import { AccessRoleMap } from '../../models/access-role'
import DomainUserModel from '../../models/access-control/domain-user.model'
import { observer } from 'mobx-react'
import AccountUserModel from '../../models/access-control/account-user.model'
import { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'
import { Flex, Modal, Popover, Radio, Select, Space, Table } from 'antd'
import { OrgAccessControl, OrgAccessDisclosure, IOrgAccessRoleOption } from '@pushly/aqe/lib/components'
import { ORG_ACCESS_DOMAIN_HINT } from '@pushly/models/lib/enums/rbac'
import { AntdClassnameTokens } from '../../styles/antd-classname-tokens'

export interface IUserAccountAccessView {
    user: UserDto & any
    acs: AccessControlService
    account: { id: number }
    domainId?: number
    singleDomain?: boolean
    onChange?: (acs: AccessControlService) => any
    mode?: 'default' | 'stand-alone' | 'nested' | 'read-only' | 'add-user'
    disabled?: boolean
    /**
     * Organization access renders as the first section here rather than above the
     * well, so it takes the same table treatment as the domain sections. Omitted
     * in a domain context, where the page is scoped to one domain and organization
     * access is not the viewer's to set.
     */
    orgAccess?: IOrgAccessSection
}

export interface IOrgAccessSection {
    options: IOrgAccessRoleOption[]
    value: number
    readOnly: boolean
    disabled: boolean
    onChange: (roleId: number) => void
}

type DomainRecord = PlatformUserDomainRecord | DomainDto

export interface IUserAccountAccessViewState {
    domains: DomainRecord[]
    loadingDomains: boolean
    selectedAccessRole: number | undefined
}

@observer
export class UserAccountAccessView extends React.Component<IUserAccountAccessView, IUserAccountAccessViewState> {
    protected appState: AppState
    protected appService: AppService
    protected userService: UserService
    protected domainService: DomainService
    protected accountService: AccountService

    public constructor(props: IUserAccountAccessView) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userService = Container.get(UserService)
        this.domainService = Container.get(DomainService)
        this.accountService = Container.get(AccountService)

        this.state = {
            loadingDomains: true,
            domains: [],
            selectedAccessRole: undefined,
        }
    }

    public componentDidMount(): void {
        this.fetchDomains()
    }

    public render() {
        const { mode } = this.props
        const isNestedMode = mode === 'nested' || mode === 'add-user'

        return (
            <div
                className={getClassNames('user-access-view', `mode-${this.props.mode ?? 'default'}`, {
                    ['disabled']: this.props.disabled,
                    ['mode-nested']: isNestedMode,
                    ['has-changes']: this.props.acs.hasChanges,
                })}
            >
                {this.renderAccessTables()}
            </div>
        )
    }

    protected renderAccessTables() {
        const modeIsAddUser = this.props.mode === 'add-user'
        const platformUserIsOrgAdmin = this.appState.abilityStore.can(
            AbilityAction.MANAGE,
            this.appState.abilityStore.currentOrgIdentity,
        )

        const allDomains = this.state.domains ?? []
        const hasMultipleDomains = allDomains.length > 1
        const hasDomainFilter = !!this.props.domainId

        const disabledRoleIds: AccessRoleId[] = [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]
        let altDomainDefaultRoleId: AccessRoleId | undefined
        // default to external no-access (internal users can only be created in halo)
        const currDomainDefaultRoleId = this.props.user?.isInternalUser
            ? AccessRoleId.INTERNAL_USER
            : AccessRoleId.EXTERNAL_USER

        if (modeIsAddUser) {
            disabledRoleIds.push(AccessRoleId.INTERNAL_NO_ACCESS)
            altDomainDefaultRoleId = AccessRoleId.EXTERNAL_NO_ACCESS
        }

        const primaryDomain = allDomains.find((domain) => domain.id.toString() === this.props.domainId?.toString())
        const altDomains = allDomains.filter((d) => d.id.toString() !== this.props.domainId?.toString())

        let display
        if (!hasDomainFilter) {
            display = (
                <Flex vertical justify="space-between" gap={8}>
                    {this.renderOrgAccessTable()}
                    {this.selectAccessLevel()}
                    {this.renderAccessTable(allDomains, 'Domains')}
                </Flex>
            )
        } else if (this.isLoading || !primaryDomain) {
            display = <AntdTableEmptyPlaceholder text={`Loading Domain Access`} icon="global" />
        } else if (platformUserIsOrgAdmin && hasMultipleDomains) {
            display = (
                <Flex vertical>
                    {this.selectAccessLevel()}

                    {this.renderAccessTable(primaryDomain, 'Current Domain', {
                        disabledRoleIds,
                        defaultRoleId: currDomainDefaultRoleId,
                    })}

                    {this.renderAccessTable(altDomains, 'Other Domains in your Organization', {
                        defaultRoleId: altDomainDefaultRoleId,
                    })}
                </Flex>
            )
        } else {
            display = this.renderAccessTable(primaryDomain, 'Current Domain', {
                disabledRoleIds,
                defaultRoleId: currDomainDefaultRoleId,
            })
        }
        return display
    }

    /**
     * One row, rendered with the same table treatment as the domain sections so the
     * two axes read as siblings rather than as a control bolted above a table.
     */
    protected renderOrgAccessTable() {
        const orgAccess = this.props.orgAccess
        if (!orgAccess) {
            return null
        }

        return (
            <Table<{ key: string }>
                title={() => (
                    <Space size={6}>
                        <span>Organization</span>
                        <Popover
                            classNames={{ content: getClassNames('popover', 'user-management-info-popover') }}
                            content={<OrgAccessDisclosure options={orgAccess.options} />}
                        >
                            <InfoCircleOutlined
                                className="info-icon"
                                aria-label="What organization access grants"
                            />
                        </Popover>
                    </Space>
                )}
                rowKey={(row) => row.key}
                className={getClassNames('user-domain-access', this.props.mode !== 'default' ? 'ghost' : undefined)}
                classNames={AntdClassnameTokens.Table({ prefix: 'user-access' })}
                dataSource={[{ key: 'org' }]}
                loading={
                    this.isLoading && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                pagination={false}
                showHeader={false}
            >
                <Table.Column
                    key="name"
                    className="name"
                    title="Organization"
                    render={() => (
                        <TableRowEntityDisplay
                            title="Access to this organization"
                            status={ORG_ACCESS_DOMAIN_HINT}
                        />
                    )}
                />
                <Table.Column
                    key="access"
                    className="access"
                    title="Access"
                    width={200}
                    render={() => (
                        <OrgAccessControl
                            layout="control"
                            options={orgAccess.options}
                            value={orgAccess.value}
                            onChange={orgAccess.onChange}
                            readOnly={orgAccess.readOnly}
                            disabled={orgAccess.disabled}
                            variant={this.props.mode === 'default' ? 'radio' : 'select'}
                        />
                    )}
                />
            </Table>
        )
    }

    protected renderAccessTable(
        domains: DomainRecord | DomainRecord[],
        title?: React.ReactNode,
        opts: {
            disabledRoleIds?: AccessRoleId[]
            defaultRoleId?: AccessRoleId
        } = {},
    ) {
        domains = Array.isArray(domains) ? domains : [domains]

        return (
            <Table<DomainRecord>
                title={title ? () => title : undefined}
                rowKey={(domain) => domain.name}
                className={getClassNames('user-domain-access', this.props.mode !== 'default' ? 'ghost' : undefined)}
                classNames={AntdClassnameTokens.Table({ prefix: 'user-access' })}
                dataSource={domains}
                loading={
                    this.isLoading && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Domains" icon="global" />,
                }}
                pagination={false}
                showHeader={false}
            >
                <Table.Column key="name" className="name" title="Domain" render={this.renderDomain} />
                <Table.Column
                    key="access"
                    className="access"
                    title="Access"
                    render={(_, domain: DomainDto) => this.renderAccessPanel(_, domain, opts)}
                    width={200}
                />
            </Table>
        )
    }

    protected renderDomain = (_: any, domain: DomainDto) => {
        return <TableRowEntityDisplay title={domain.displayName} status={domain.name} />
    }

    protected selectAccessLevel = () => {
        if (this.props.mode !== 'read-only') {
            return (
                <Well variant="ghost" padding="small" hideHeader>
                    <Flex justify="space-between" gap={8}>
                        <div>
                            <b>Bulk Apply User Domain Access</b>
                        </div>
                        {this.bulkApplyAccessLevelSelect()}
                    </Flex>
                </Well>
            )
        } else return
    }

    protected bulkApplyAccessLevelSelect() {
        if (this.props.mode === 'read-only') {
            return null
        }

        let availableRoleIds = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds
        const apiRoleIds = [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]

        if (this.props.user?.id && !this.props.user?.isApiUser) {
            availableRoleIds = availableRoleIds.filter((id) => !apiRoleIds.includes(id))
        }

        return (
            <Select
                size="small"
                disabled={this.props.user?.isApiUser || this.props.user?.isInternalDevUser}
                onChange={this.renderConfirmModal}
                value={this.state.selectedAccessRole}
                placeholder="Bulk apply access level"
                style={{ minWidth: 190 }}
            >
                {availableRoleIds.map((id) => (
                    <Select.Option key={id} value={id}>
                        {AccessRoleMap.get(id)?.label}
                    </Select.Option>
                ))}
            </Select>
        )
    }

    protected clearSelectedAccessRole = () => {
        this.setState({ selectedAccessRole: undefined })
    }

    protected renderConfirmModal = (roleId: number) => {
        this.setState({ selectedAccessRole: roleId })
        const roleName = AccessRoleMap.get(roleId)?.label

        return Modal.confirm({
            okText: 'Yes, Apply',
            okType: 'primary',
            cancelText: 'Cancel',
            title: 'Bulk Domain Access Assignment',
            content: (
                <div>
                    <p>
                        Are you sure you want to apply <b>{roleName?.toLowerCase()}</b> access to all domains?
                    </p>
                </div>
            ),
            onOk: () => {
                this.clearSelectedAccessRole()
                return this.updateAccountDomainRolesFromSelect(roleId)
            },
            onCancel: this.clearSelectedAccessRole,
        })
    }

    protected renderAccessPanel = (
        _: any,
        domain: DomainDto,
        opts: {
            disabled?: boolean
            disabledRoleIds?: AccessRoleId[]
            defaultRoleId?: AccessRoleId
        } = {},
    ) => {
        const { mode } = this.props
        const modeIsDefault = !mode || mode === 'default'
        const modeIsReadOnly = mode === 'read-only'
        const disabledRoleIds = opts?.disabledRoleIds ?? [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]
        let roleId = opts?.defaultRoleId ?? AccessRoleId.EXTERNAL_NO_ACCESS
        const domainRecord = this.props.acs.getDomainRecord(domain.id)
        if (domainRecord) {
            roleId = domainRecord.getRoleId()
        }

        const roleIdOptions = this.props.user.isInternalUser ? InternalAccessRoleIds : ExternalAccessRoleIds
        const visibleRoleIdOptions = roleIdOptions.filter((id) => !disabledRoleIds.includes(id))
        const invalidRoleTypeCombination = !visibleRoleIdOptions.includes(roleId)

        // ensure invalid assignments show as no assignment
        const currentRoleIdValue = invalidRoleTypeCombination ? undefined : roleId

        let roleWarning
        if (invalidRoleTypeCombination) {
            roleWarning = (
                <Popover content={'Invalid role selection'}>
                    <WarningOutlined className="access-warning" />
                </Popover>
            )
        }

        let display
        if (modeIsReadOnly) {
            display = (
                <Space className="ro-mode-role-name">
                    {roleWarning}
                    {AccessRoleMap.get(roleId)?.label}
                </Space>
            )
        } else if (modeIsDefault) {
            display = (
                <Space>
                    {roleWarning}
                    <Radio.Group
                        disabled={opts?.disabled}
                        value={currentRoleIdValue}
                        size="small"
                        onChange={(ev) => this.updateAccountDomainRole(domain, ev.target.value)}
                    >
                        {visibleRoleIdOptions.map((key) => (
                            <Radio key={key} value={key}>
                                {AccessRoleMap.get(key)?.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Space>
            )
        } else {
            display = (
                <Space>
                    {roleWarning}
                    <Select<AccessRoleId>
                        disabled={opts?.disabled}
                        value={currentRoleIdValue}
                        size="small"
                        onChange={(value) => this.updateAccountDomainRole(domain, value)}
                        style={{ width: 200 }}
                    >
                        {visibleRoleIdOptions.map((key) => (
                            <Select.Option key={key} value={key}>
                                {AccessRoleMap.get(key)?.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
            )
        }

        return display
    }

    protected get isLoading(): boolean {
        return this.state.loadingDomains ?? false
    }
    protected async updateAccountDomainRole(domain: DomainDto, roleId: AccessRoleId) {
        const shouldAutoPersist = this.props.mode !== 'nested' && this.props.mode !== 'add-user'

        const accessRole = AccessRoleMap.get(roleId)

        if (accessRole) {
            // use the clean account record when it exists so we don't
            // accidentally mark the org record as dirty when only a
            // domain role changed
            let orgRecord = this.props.acs.getAccountRecord(domain.accountId)
            if (!orgRecord) {
                const noAccessRole = AccessRoleMap.get(this.props.acs.noAccessRoleId)
                orgRecord = new AccountUserModel({
                    accountId: domain.accountId,
                    // required by the create endpoint; serialize() drops undefined
                    // keys, so omitting it sends a payload the API rejects
                    userId: this.props.user.id,
                    roleId: noAccessRole!.id,
                    roleName: noAccessRole!.name,
                    flags: [],
                })
            }

            let domainRecord = this.props.acs.getDomainRecord(domain.id)?.clone()

            if (domainRecord) {
                domainRecord.setRole(accessRole)
            } else {
                domainRecord = new DomainUserModel({
                    accountUserId: orgRecord.getId(),
                    domainId: domain.id,
                    roleId: accessRole.id,
                    roleName: accessRole.name,
                    flags: [],
                })
            }

            await this.props.acs.updateCachedRecord(orgRecord.getAccountId(), orgRecord, domainRecord)

            if (shouldAutoPersist) {
                try {
                    await this.props.acs.persistChanges()
                } catch (err) {
                    simpleFormErrorNotification(err)
                }
            }

            this.props.onChange?.(this.props.acs)
        }
    }

    protected async updateAccountDomainRolesFromSelect(roleId: AccessRoleId) {
        const shouldAutoPersist = this.props.mode !== 'nested' && this.props.mode !== 'add-user'

        const accessRole = AccessRoleMap.get(roleId)

        const firstDomain = this.state.domains[0] ? this.state.domains[0] : null
        const domainRecords: DomainUserModel[] = []

        if (accessRole && firstDomain) {
            // use the clean account record when it exists so we don't
            // accidentally mark the org record as dirty when only
            // domain roles changed
            let orgRecord = this.props.acs.getAccountRecord(firstDomain.accountId)
            if (!orgRecord) {
                const noAccessRole = AccessRoleMap.get(this.props.acs.noAccessRoleId)
                orgRecord = new AccountUserModel({
                    accountId: firstDomain.accountId,
                    // required by the create endpoint; serialize() drops undefined
                    // keys, so omitting it sends a payload the API rejects
                    userId: this.props.user.id,
                    roleId: noAccessRole!.id,
                    roleName: noAccessRole!.name,
                    flags: [],
                })
            }

            this.state.domains.forEach((domain) => {
                let domainRecord = this.props.acs.getDomainRecord(domain.id)?.clone()

                if (domainRecord) {
                    domainRecord.setRole(accessRole)
                } else {
                    domainRecord = new DomainUserModel({
                        accountUserId: orgRecord?.getId(),
                        domainId: domain.id,
                        roleId: accessRole.id,
                        roleName: accessRole.name,
                        flags: [],
                    })
                }
                domainRecords.push(domainRecord)
            })

            await this.props.acs.updateCachedRecords(orgRecord.getAccountId(), orgRecord, domainRecords)

            if (shouldAutoPersist) {
                try {
                    await this.props.acs.persistChanges()
                } catch (err) {
                    simpleFormErrorNotification(err)
                }
            }

            this.props.onChange?.(this.props.acs)
        }
    }

    protected async fetchDomains(): Promise<void> {
        if (!this.props.account) {
            setTimeout(() => this.fetchDomains(), 50)
            return
        }

        let domains: DomainRecord[] =
            this.appState.currentUserDomains?.filter((d) => d.accountId === this.props.account.id) ?? []
        this.setState({
            loadingDomains: !domains.length,
            domains,
        })

        if (!domains.length) {
            const newState = {
                loadingDomains: false,
                domains,
            }

            const { ok, data } = await this.domainService.fetchByAccountId(this.props.account.id, {
                query: { pagination: false },
                showLoadingScreen: false,
                cancellationKey: `user-view-fetch-account-${this.props.account.id}-domains`,
            })

            if (ok) {
                newState.domains = data
            }

            this.setState(newState)
        }
    }
}
